<!-- eslint-disable vuejs-accessibility/mouse-events-have-key-events -->
<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<!-- eslint-disable tailwindcss/no-custom-classname -->
<template>
  <ul class="flex h-full items-center gap-32 px-4">
    <li class="h-full">
      <RevLink
        class="body-2 hover:body-2-bold text-nowrap hover:no-underline"
        :to="tradeInUrl"
        :tracking="tradeInTracking"
        :underlined="false"
      >
        <div
          class="relative flex h-full items-center text-nowrap after:bg-action-default-hi after:pointer-events-none after:absolute after:-bottom-[1px] after:left-0 after:h-[2px] after:w-full after:opacity-0 after:content-[''] hover:after:opacity-100"
        >
          <IconSwap />
          <span class="bold-hack" :title="i18n(translations.tradeIn)">{{
            i18n(translations.tradeIn)
          }}</span>
        </div>
      </RevLink>
    </li>

    <li ref="careMenuItemElement" class="h-full">
      <div class="h-full" @mouseenter="showMenu" @mouseleave="hideMenu">
        <button
          :aria-controls="`submenu-${CARE_DROPDOWN_ID}`"
          :aria-expanded="isCareDropdownOpen"
          aria-haspopup="true"
          class="body-2 hover:body-2-bold flex h-full items-center border-0 bg-transparent p-0 text-nowrap hover:no-underline"
          role="menuitem"
          type="button"
          @click="isCareDropdownOpen ? hideMenu() : showMenu()"
        >
          <div
            class="relative flex h-full items-center text-nowrap after:bg-action-default-hi after:pointer-events-none after:absolute after:-bottom-[1px] after:left-0 after:h-[2px] after:w-full after:opacity-0 after:content-[''] hover:after:opacity-100"
            :class="{ 'after:opacity-100': isCareDropdownOpen }"
          >
            <IconQuality />
            <span
              class="bold-hack"
              :title="i18n(translations.extendedServices)"
              >{{ i18n(translations.extendedServices) }}</span
            >
          </div>
        </button>

        <div class="absolute inset-x-0 z-[1] mt-1">
          <Transition
            :css="withTransition"
            enter-active-class="ease-out duration-300"
            enter-from-class="[clip-path:inset(0_0_100%)]"
            enter-to-class="[clip-path:inset(0_0_0%)]"
            leave-active-class="ease-in duration-100"
            leave-from-class="[clip-path:inset(0_0_0%)]"
            leave-to-class="[clip-path:inset(0_0_100%)]"
            @after-enter="withTransition = false"
          >
            <div
              v-show="isCareDropdownOpen"
              class="bg-surface-default-low border-static-default-low border-t transition-[clip-path]"
            >
              <div
                :id="`submenu-${CARE_DROPDOWN_ID}`"
                class="mx-auto grid max-w-[1120px] grid-cols-3 gap-12 px-40 pb-40 pt-24"
                role="menu"
              >
                <NavServiceCard
                  v-for="card in cards"
                  :key="card.title"
                  :cta-label="card.ctaLabel"
                  :cta-url="card.ctaUrl"
                  :description="card.description"
                  :image="card.image"
                  :title="card.title"
                  :tracking="{
                    zone: 'mega_menu',
                    name: 'services',
                    value: card.trackingValue,
                  }"
                />
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </li>

    <li v-if="mobilePlansUrl" class="h-full">
      <RevLink
        class="body-2 hover:body-2-bold text-nowrap hover:no-underline"
        :to="mobilePlansUrl"
        :tracking="mobilePlansTracking"
        :underlined="false"
      >
        <div
          class="relative flex h-full items-center text-nowrap after:bg-action-default-hi after:pointer-events-none after:absolute after:-bottom-[1px] after:left-0 after:h-[2px] after:w-full after:opacity-0 after:content-[''] hover:after:opacity-100"
        >
          <IconSmartphone />
          <span class="bold-hack" :title="i18n(translations.mobilePlans)">{{
            i18n(translations.mobilePlans)
          }}</span>
        </div>
      </RevLink>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevLink } from '@ds/components/Link'
import { IconQuality } from '@ds/icons/IconQuality'
import { IconSmartphone } from '@ds/icons/IconSmartphone'
import { IconSwap } from '@ds/icons/IconSwap'
import { useFocusWithin } from '@vueuse/core'

import { useActiveMenu } from '../../composables/useActiveMenu'
import { useExtendedServicesCards } from '../../composables/useExtendedServicesCards'
import { useMobilePlansUrl } from '../../composables/useMobilePlansUrl'
import { useTradeInUrl } from '../../composables/useTradeInUrl'

import NavServiceCard from './NavServiceCard.vue'
import translations from './NavServicesMenu.translations'

const CARE_DROPDOWN_ID = 'services-care-and-repair'

const i18n = useI18n()

const { tradeInUrl } = useTradeInUrl()
const { mobilePlansUrl } = useMobilePlansUrl()

const tradeInTracking = {
  zone: 'mega_menu',
  name: 'trade_in',
  value: 'trade_in',
}
const mobilePlansTracking = {
  zone: 'mega_menu',
  name: 'mobile_plans',
  value: 'mobile_plans',
}

const { cards } = useExtendedServicesCards()

const careMenuItemElement = ref()
const { activeMenu, isMenuActive, setActiveMenu, clearActiveMenu } =
  useActiveMenu()
const isCareDropdownOpenLocal = ref(false)
const isCareDropdownOpen = computed(
  () => isCareDropdownOpenLocal.value && activeMenu.value === CARE_DROPDOWN_ID,
)

let timeout: ReturnType<typeof setTimeout>

function showMenu() {
  clearTimeout(timeout)
  timeout = setTimeout(() => {
    isCareDropdownOpenLocal.value = true
    setActiveMenu(CARE_DROPDOWN_ID)
  }, 150)
}

function hideMenu() {
  clearTimeout(timeout)
  timeout = setTimeout(() => {
    isCareDropdownOpenLocal.value = false
    clearActiveMenu()
  }, 150)
}

const withTransition = ref(true)
watch(isMenuActive, () => {
  if (!isMenuActive.value) {
    withTransition.value = true
  }
})

const { focused } = useFocusWithin(careMenuItemElement)
watch(focused, (isFocused) => {
  if (!isFocused) {
    hideMenu()
  }
})

onBeforeUnmount(() => {
  clearTimeout(timeout)
})
</script>

<style scoped>
.bold-hack {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
}

.bold-hack::after {
  content: attr(title);
  font-weight: 600;
  font-size: 0.875rem /* 14px */;
  line-height: 1.25rem /* 20px */;
  height: 0;
  overflow: hidden;
  visibility: hidden;
}
</style>
